import { JSQ_PHONE, JSQ_SUPPORT_EMAIL, JSQ_TEXT } from "common/constants/copy";
import {
  Accordion,
  AccordionGroup,
  Flex,
  FullscreenTakeover,
  OrderedList,
  OrderedListItem,
  Text,
} from "@jsq/ds/core";
import React from "react";
import BulletedList from "../BulletedList";
import styles from "./styles.scss";

type Props = {
  onExit: () => void;
};

export const FAQTakeover = ({ onExit }: Props) => {
  const contactText = (
    <>
      please contact{" "}
      <a className="link" href={`mailto:${JSQ_SUPPORT_EMAIL}`}>
        {JSQ_SUPPORT_EMAIL}
      </a>{" "}
      or{" "}
      <a className="link" href={`tel:${JSQ_PHONE}`} data-cy="arena-phone">
        {JSQ_PHONE}
      </a>{" "}
      for help.
    </>
  );

  const textMessageLink = (
    <a className="link" href={`tel:${JSQ_TEXT}`} data-cy="arena-phone">
      {JSQ_TEXT}
    </a>
  );
  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header onExit={onExit} title="Login and password reset FAQ" />
      <FullscreenTakeover.Body>
        <div className={styles.FAQTakeover__accordionGroup}>
          <AccordionGroup>
            <Accordion title="I tried to reset my password, but I don't see the password reset email">
              <Flex flexDirection="column" className="stacked-md">
                <Text marginBottom="sm">
                  To begin troubleshooting this issue, follow these steps:
                </Text>
                <OrderedList>
                  <OrderedListItem>Check your Spam and/or Junk folders</OrderedListItem>
                  <OrderedListItem>
                    <Text>
                      Clear your browser&apos;s cache and cookies (see:{" "}
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://support.google.com/accounts/answer/32050?hl=en&co=GENIE.Platform%3DDesktop"
                        className="link"
                      >
                        How to clear cache and cookies in Chrome
                      </a>
                      )
                    </Text>
                  </OrderedListItem>
                  <OrderedListItem>
                    Try reopening your email in a Chrome Incognito window
                  </OrderedListItem>
                  <OrderedListItem>
                    Allowlist <b>no-reply@junipersquare.com</b> or add it as a contact in your
                    email settings (mark as safe sender and not spam)
                  </OrderedListItem>
                </OrderedList>
              </Flex>
              <Text>
                If you still haven’t received your password reset email after attempting the
                above, {contactText}
              </Text>
            </Accordion>
            <Accordion title="I haven’t received a 2FA code sent via SMS to my phone">
              <Flex flexDirection="column">
                <Text marginBottom="md">
                  <b>Note</b>: If you are traveling abroad, it may affect your ability to
                  receive SMS messages sent from Juniper Square due to country-specific
                  limitations. You can still proceed by entering one of your Recovery Codes to
                  bypass the 2FA requirement. You received your Recovery Codes after activating
                  your account. Once signed in, go to your profile settings to update your 2FA
                  method. If you don&apos;t have your Recovery Codes, try the steps below.
                </Text>
                <Text marginBottom="sm">
                  Here are some troubleshooting steps to help you start receiving 2FA text
                  messages:
                </Text>
                <div className="stacked-md">
                  <OrderedList>
                    <OrderedListItem>
                      <Text>
                        Text “START” to {textMessageLink}. If you’ve accidentally opted out,
                        texting START to this number should re-enroll you in receiving 2FA
                        messages. Once completed, try signing in again to see if you receive a
                        code.
                      </Text>
                    </OrderedListItem>
                    <OrderedListItem>
                      <Text marginBottom="sm">Check your phone’s blocked list.</Text>
                      <BulletedList>
                        <BulletedList.Item>
                          <Text>
                            On <b>iPhone</b>, go to Settings {">"} Messages, then scroll down
                            until you see the Block Contacts option and tap. If you see the
                            number, {textMessageLink}, unblock it and retry sending the code.
                          </Text>
                        </BulletedList.Item>
                        <BulletedList.Item>
                          <Flex flexDirection="row">
                            <Text>
                              On <b>Android</b>, follow{" "}
                              <a
                                target="_blank"
                                rel="noreferrer"
                                className="link"
                                href="https://support.google.com/phoneapp/answer/6325463?hl=en"
                              >
                                these instructions
                              </a>
                              .
                            </Text>
                          </Flex>
                        </BulletedList.Item>
                      </BulletedList>
                    </OrderedListItem>
                    <OrderedListItem>
                      <Flex flexDirection="row">
                        <Text>
                          Clear the cache on your phone (see instructions for{" "}
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="link"
                            href="https://support.apple.com/en-us/HT201265"
                          >
                            iPhone
                          </a>{" "}
                          and{" "}
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="link"
                            href="https://www.businessinsider.com/guides/tech/how-to-clear-cache-on-android-phone"
                          >
                            Android
                          </a>
                          )
                        </Text>
                      </Flex>
                    </OrderedListItem>
                    <OrderedListItem>
                      <Text>
                        Turn your phone off and back on to reset the network connection
                      </Text>
                    </OrderedListItem>
                  </OrderedList>
                </div>
                <Text>If you still need assistance, {contactText}</Text>
              </Flex>
            </Accordion>
            <Accordion title="I’m trying to sign in, but I don’t recognize the phone number the 2FA code is being sent to">
              <Flex flexDirection="column">
                <Text marginBottom="md">
                  If you use the same email address to log in to multiple investor portal
                  accounts, those accounts are now consolidated for you into one single login as
                  part of an upgraded Juniper Square investor portal experience.
                </Text>
                <Text marginBottom="md">
                  As part of this consolidation, you will need to have access to the phone
                  number(s) being used for 2FA, for each account associated with your email
                  address.
                </Text>
                <Text marginBottom="md">
                  As a one-time security verification, you will be required to enter the code
                  from each 2FA phone number that your email address is associated with. After
                  signing in, under Settings, you can save your preferred phone number as your
                  2FA method, or choose email verification for 2FA.
                </Text>
                <Text marginBottom="md">
                  <b>Tip</b>: If you share a portal account with another individual (ex: a
                  spouse), you may need to coordinate with them to retrieve the necessary codes.
                </Text>
                <Text>If you need further assistance, {contactText}</Text>
              </Flex>
            </Accordion>
          </AccordionGroup>
        </div>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
